import {
  calculatePointsPCA,
  GeocentricCoordinate,
  geometryToArray,
} from '../projections';
import MeasurementController from './measurement-controller';
import {getArea, getLength} from 'ol/sphere';
import {LineString, Polygon} from 'ol/geom';
import Delaunator from 'delaunator';
import earcut from 'earcut';
import {isMobile, randomHexColor} from '../misc';
import {nanoid} from '@reduxjs/toolkit';
import { pointInPolygon } from '../utilities/polygon';
import {
  Vector3,
  BufferGeometry,
  BufferAttribute,
  PointsMaterial,
  Points,
  Geometry,
  MeshBasicMaterial,
  Face3,
  Mesh,
  EdgesGeometry,
  LineBasicMaterial,
  LineSegments,
  Triangle,
  DoubleSide,
  MathUtils
} from 'three';

export class Measurement {
  public controller: MeasurementController;
  public id = nanoid();
  public dbType = null;
  public databaseID = null;
  public displayType = 'Measurement';
  public title = 'Measurement';
  public timeCreated = new Date();
  public startClick = isMobile() ? "Tap" : "Click";
  public cancelClick = isMobile() ? "Long-press" : "Right-click";
  public finishClick = isMobile() ? "Double-tap" : "Double-click";
  public tooltipStatic = {
    start: null,
    end: null,
    more: null
  };

  setController(controller) {
    this.controller = controller;
  }

  get timestamp() {
    return Math.floor(this.timeCreated.getTime() / 1000);
  }

  set timestamp(isoTime) {
    this.timeCreated = new Date(isoTime);
  }

  get startTooltip() {
    return this.tooltipStatic.start;
  }

  get moreTooltip() {
    return this.tooltipStatic.more;
  }

  get endTooltip() {
    return this.tooltipStatic.end;
  }

  get viewer() {
    return this.controller.viewer;
  }

  get valid() {
    return true;
  }

  get formatter() {
    return this.controller.formatter;
  }

  clear() {}
}

export class Measurement3D extends Measurement {
  public vertices:Array<GeocentricCoordinate> = [];
  public maxVertexLength = 2;
  public minVertexRequired = 2;

  constructor() {
    super();

    this.displayType = '3D Measurement';
    this.title = '3D Measurement';
    this.dbType = "length";

    this.tooltipStatic.start =`${this.startClick} to start line`;
  }

  get numVertices() {
    return this.coordinates.length;
  }

  /** Return 3d distance in meters, based on scene view coordinates */
  get distance3D() {
    return this.getLineDistance(false);
  }

  /** Return 2d distance in meters, based on scene view coordinates */
  get distance2D() {
    return this.getLineDistance(true);
  }

  /** Get measurement height difference in meters, based on scene view coordinates */
  get rise() {
    // Currently uses the first + last point
    let lastIndex = this.vertices.length - 1;
    let firstPoint = this.vertices[0].toScene();
    let lastPoint  = this.vertices[lastIndex].toScene();
    let height = Math.abs(lastPoint.z - firstPoint.z);

    return height;
  }

  get angle() {
    let numberOfPoints = this.vertices.length;
    if (numberOfPoints !== 2) {
      return NaN;
    }

    let angle = Math.atan(this.rise / this.distance2D);
    return MathUtils.radToDeg(angle);
  }

  get grade() {
    let numberOfPoints = this.vertices.length;
    if (numberOfPoints !== 2) {
      return NaN;
    }

    return 100 * (this.rise / this.distance2D);
  }

  get coordinates() {
    return this.vertices.map(vertex => {
      return vertex.toArray();
    });
  }

  get sceneVertices() {
    return this.vertices.map(vertex => {
      return vertex.toScene().toArray();
    });
  }

  add(vertex, parent=null) {
    let finished = false;
    let addedPoint = false;
    vertex = new GeocentricCoordinate().fromScene(vertex);

    if (this.vertices.length === 0) {
      this.vertices.push(vertex);
      addedPoint = true;
    } else {
      // We only add a new point if the distance travelled is large enough
      let oldVertex = this.vertices[this.vertices.length - 1];
      if (oldVertex.distanceTo(vertex) > 0.10) {
        // New point is far enough away from previous point
        this.vertices.push(vertex);
        addedPoint = true;
      } else {
        // Clicked the same point twice, so we finish the measurement
        // if we have gathered enough points
        if (this.vertices.length >= this.minVertexRequired) {
          finished = true;
        }
      }
    }

    // For types with a fixed number of points, set finished to
    // true if we have reached the max number of points
    if (!finished) {
      finished = this.vertices.length === this.maxVertexLength;
    }

    return {
      finished: finished,
      added: addedPoint,
      valid: true
    };
  }

  getProjectedVertices(projection) {
    let vertices = this.vertices.map(vertex => {
      return vertex.toProjection(projection).toArray();
    });

    if (this instanceof MeasurementArea) {
      vertices.push(vertices[0]);
    }

    return vertices;
  }

  /** Private */
  getLineDistance(ignoreHeight) {
    let vertexA;
    let vertexB;

    let distance = 0;
    let vertices = this.vertices;

    for (var i = 1; i < vertices.length; i++) {
      let prevIndex = i - 1;
      vertexA = vertices[prevIndex].toScene();
      vertexB = vertices[i].toScene();

      if (ignoreHeight) {
        vertexA.z = 0;
        vertexB.z = 0;
      }

      distance += vertexA.distanceTo(vertexB);
    }

    return distance;
  }
}

export class MeasurementLine extends Measurement3D {
  constructor() {
    super();

    this.maxVertexLength = 16;
  }

  get moreTooltip() {
    return `
      ${this.startClick} to add more points (${this.vertices.length})
      <br>${this.finishClick} to finish
      <br>${this.cancelClick} to cancel
    `;
  }

  get endTooltip() {
    return this.moreTooltip;
  }
}

export class MeasurementPoint extends Measurement3D {
  constructor() {
    super();

    this.displayType = '3D Point';
    this.title = '3D Point';
    this.dbType = 'point';
    this.maxVertexLength = 1;
    this.minVertexRequired = 1;

    this.tooltipStatic.start = 'Click for single point';
  }
}

export class MeasurementArea extends Measurement3D {
  public surfaceGenerator;
  public areaInvalidMessage;

  constructor() {
    super();

    this.displayType = '3D Area';
    this.title = '3D Area';
    this.dbType = 'area';
    this.maxVertexLength = 16;
    this.minVertexRequired = 3;
    this.areaInvalidMessage = "Invalid Area";

    this.tooltipStatic.start = `${this.startClick} to start area`;
  }

  get moreTooltip() {
    return `
      ${this.startClick} to add more points (${this.vertices.length})
      <br>${this.cancelClick} to cancel
    `;
  }

  get endTooltip() {
    return `
      ${this.startClick} to add more points (${this.vertices.length})
      <br>${this.finishClick} to finish
      <br>${this.cancelClick} to cancel
    `;
  }

  get localVertices3D() {
    return this.vertices.map(vertex => {
      let vertexLocal = vertex.toScene();
      return vertexLocal;
    });
  }

  get indices() {
    return this.surfaceGenerator.indices;
  }

  get area3D() {
    return this.surfaceGenerator.area;
  }

  get valid() {
    return this.surfaceGenerator.valid;
  }

  setController(controller) {
    this.controller = controller;
    this.surfaceGenerator = new SurfaceGenerator(this);
  }

  generateSurface() {
    this.surfaceGenerator.clear();
    this.surfaceGenerator.generate();
  }

  clear() {
    this.surfaceGenerator.clear();
  }
}

export class MeasurementVolume extends MeasurementArea {
  public volumeHelper;
  public invalidVolumeMessage;

  constructor() {
    super();

    this.displayType = '3D Volume';
    this.title = '3D Volume';
    this.dbType = 'volume';
    this.invalidVolumeMessage = "Invalid Volume";

    this.tooltipStatic.start = `${this.startClick} to start volume`;
  }

  get calculated() {
    return this.volumeHelper.calculated;
  }

  get calculating() {
    return this.volumeHelper.calculatingVolume;
  }

  get volumeValue() {
    return this.volumeHelper.value;
  }

  calculate() {
    this.volumeHelper.calculate();
  }

  recalculate() {
    this.volumeHelper.clear();
    this.volumeHelper.calculate();
  }

  /** Cancel webworker and pass empty array as results */
  stopVolumeCalculation() {
    this.volumeHelper.stopCalculation();
  }

  setController(controller) {
    this.controller = controller;

    let pointCloud = controller.pointclouds;
    this.volumeHelper = new VolumeHelper(this);
    this.volumeHelper.init(pointCloud);
    this.surfaceGenerator = new SurfaceGenerator(this);
  }

  loadExistingVolume(value) {
    this.volumeHelper.setVolume(value);
  }

  clear() {
    this.volumeHelper.destroyWorker();
    this.volumeHelper.clear();
    this.surfaceGenerator.clear();
  }
}

export class SnapMeasurement extends Measurement3D {
  constructor() {
    super();
  }

  add(vertex, parent) {
    let valid = false;
    let added = false;
    let pointCloud = parent.pointCloud;
    let snapResult = this.snap(vertex, pointCloud);
    this.vertices.push(new GeocentricCoordinate().fromScene(vertex));

    if (snapResult) {
      valid = true;
      added = true;
      this.vertices.push(new GeocentricCoordinate().fromScene(snapResult));
    }

    return {
      finished: true,
      added: added,
      valid: valid
    };
  }

  snap(vertex, pointCloud) {
    let verticalTolerance = 0.30;
    let finalHeight;
    let validHeights;

    let pos = new Vector3().copy(vertex);
    let points = pointCloud.pointsInRadius(pos, 0.15);
    let heights = points.map(point => point.z);

    if (this instanceof SnapDownMeasurement) {
      validHeights = heights.filter(height => vertex.z - height > verticalTolerance);
    } else {
      validHeights = heights.filter(height => height - vertex.z > verticalTolerance);
    }

    if (validHeights.length === 0) {
      return undefined;
    }

    if (this instanceof SnapDownMeasurement) {
      finalHeight = validHeights.reduce((acc, curr) => Math.max(acc, curr));
    } else {
      finalHeight = validHeights.reduce((acc, curr) => Math.min(acc, curr));
    }

    return new Vector3(vertex.x, vertex.y, finalHeight);
  }
}

export class SnapUpMeasurement extends SnapMeasurement {
  constructor() {
    super();

    this.tooltipStatic.start = `${this.startClick} to snap up`;
  }
}

export class SnapDownMeasurement extends SnapMeasurement {
  constructor() {
    super();

    this.tooltipStatic.start = `${this.startClick} to snap down`;
  }
}

export class AerialMeasurement extends Measurement {
  public geometry;

  constructor(geometry = null) {
    super();

    this.geometry = geometry;
    this.title = null;
    this.displayType = '2D Measurement';
  }

  get coordinates() {
    return this.geometry.getCoordinates();
  }

  get numVertices() {
    return this.coordinates[0].length;
  }

  getGeometry() {
    return this.geometry;
  }

  getProjectedVertices(projection) {
    return geometryToArray(this.geometry, projection);
  }

  getValue() {}

  loadFromCoordinates(coordinates) {}
}

export class AerialLengthMeasurement extends AerialMeasurement {
  constructor(geometry = null) {
    super(geometry);
    this.title = '2D Length';
    this.displayType = 'Length';
    this.dbType = "length";
  }

  getValue() {
    return getLength(this.geometry);
  }

  loadFromCoordinates(coordinates) {
    this.geometry = new LineString(coordinates);
  }
}

export class AerialAreaMeasurement extends AerialMeasurement {
  constructor(geometry = null) {
    super(geometry);
    this.title = '2D Area';
    this.displayType = 'Area';
    this.dbType = "area";
  }

  getValue() {
    return getArea(this.geometry);
  }

  loadFromCoordinates(coordinates) {
    this.geometry = new Polygon(coordinates);
  }
}

class VolumeHelper {
  private calculatingVolume = false;
  private calculated = false;
  private measurement: MeasurementVolume;
  private spacing = 0.5;
  private sigma = 1.0;
  private minNeighbours = 20;
  private maxNeighbours = 100;
  private neighbourPercent = 0.05;
  private maxHeightDelta = 999.0;
  private worker;
  private vertices;
  private bounds;
  private groundHeight;
  private pointCloud;
  private pointcloudmesh;
  private pointswireframe;
  private value;

  constructor(measurement) {
    this.measurement = measurement;
    this.clear();
  }

  get scene() {
    let measurement = this.measurement;
    return measurement.controller.scene;
    //return measurement.viewer.pointclouds.scene;
  }

  calculate(updateValue = true) {
    if (this.calculatingVolume) {
      return;
    }

    this.vertices = this.measurement.localVertices3D;
    this.bounds = this.getCalculatedBounds();

    // Calculate average ground height
    const heights = this.vertices.map(vertex => vertex.z);
    const sum = heights.reduce((a, b) => a + b, 0);
    this.groundHeight = (sum / heights.length) || 0;

    this.calculateVolume(updateValue);
  }

  init(pointCloud) {
    this.pointCloud = pointCloud;
    this.clear();
  }

  getCalculatedBounds() {
    let arr = this.vertices;

    let x = arr.map(vertex => vertex.x);
    let xMin = Math.min(...x);
    let xMax = Math.max(...x);

    let y = arr.map(vertex => vertex.y);
    let yMin = Math.min(...y);
    let yMax = Math.max(...y);

    let z = arr.map(vertex => vertex.z);
    let zMin = Math.min(...z);
    let zMax = Math.max(...z);

    let center = new Vector3(
      xMin + (xMax - xMin) / 2.0,
      yMin + (yMax - yMin) / 2.0,
      zMin + (zMax - zMin) / 2.0
    );

    let size = Math.max(xMax - xMin, yMax - yMin);

    return {
      center: center,
      size: size
    };
  }

  filterByHeight(points) {
    return points.filter(point => {
      let height = Math.abs(point.z - this.groundHeight);
      return height < this.maxHeightDelta;
    });
  }

  removeOutliersAsync(points, callback) {
    const workerURL = "workers/sparse-outliers.js";
    let worker = new Worker(`${process.env.PUBLIC_URL}/${workerURL}`);

    worker.onmessage = event => {
      callback(event.data);
    };

    // Remove any sparse outliers
    worker.postMessage({
      points: points,
      sigma: this.sigma,
      minNeighbours: this.minNeighbours,
      maxNeighbours: this.maxNeighbours,
      neighbourPercent: this.neighbourPercent
    });

    this.worker = worker;
  }

  destroyWorker() {
    if (this.worker) {
      this.worker.terminate();
      this.worker = undefined;
    }
  }

  drawDebugPoints(points) {
    let positions = new Float32Array(points.length * 3);
    for (var i = 0; i < points.length; i++) {
      positions[3 * i + 0] = points[i].x;
      positions[3 * i + 1] = points[i].y;
      positions[3 * i + 2] = points[i].z;
    }
    let geometry = new BufferGeometry();
    geometry.setAttribute('position', new BufferAttribute(positions, 3));
    let material = new PointsMaterial({
      color: 0x00ff00,
      size: 0.1
    });
    this.pointcloudmesh = new Points( geometry, material );
    this.scene.add(this.pointcloudmesh);
  }

  drawMesh(points, triangles) {
    // mesh
    let vertices = points;
    let geometry = new Geometry();
    let material = new MeshBasicMaterial({
      color: 0xffffff,
      side: DoubleSide
    });
    geometry.vertices = vertices;
    triangles.forEach(tri => {
      geometry.faces.push(new Face3(
        tri[0], tri[1], tri[2]
      ));
    });
    let mesh = new Mesh( geometry, material );

    // wireframe
    let geo = new EdgesGeometry(mesh.geometry);
    let mat = new LineBasicMaterial({
      color: 0xf2f2f2,
    });
    let wireframe = new LineSegments(geo, mat);
    mesh.add(wireframe);

    this.pointswireframe = mesh;
    this.scene.add(this.pointswireframe);
  }

  setVolume(value) {
    this.calculated = true;
    this.value = value;
    //this.calculate(false);
  }

  getVolume(points, triangles) {
    let volume = 0;
    triangles.forEach(tri => {
      let p1 = points[tri[0]].clone();
      let p2 = points[tri[1]].clone();
      let p3 = points[tri[2]].clone();
      let signedTetrahedronVolume = p1.dot(p2.cross(p3)) / 6.0;
      volume += signedTetrahedronVolume;
    });

    return volume;
  }

  generateTriangleArray(buffer, reverse = false) {
    let arr = Array.from(buffer);
    let v1 = arr.filter((v, index) => index % 3 === 0);
    let v2 = arr.filter((v, index) => index % 3 === 1);
    let v3 = arr.filter((v, index) => index % 3 === 2);
    let final = [];
    for (var i = 0; i < arr.length / 3; i++) {
      let vertices = [v1[i], v2[i], v3[i]];
      if (reverse) {
        vertices = [v1[i], v3[i], v2[i]];
      }
      final.push(vertices);
    }

    return final;
  }

  removeInvalidTriangles(points, triangles) {
    let validTriangles = [];
    triangles.forEach(triangle => {
      let p1 = points[triangle[0]];
      let p2 = points[triangle[1]];
      let p3 = points[triangle[2]];

      let tri = new Triangle(p1, p2, p3);
      let center = new Vector3();
      tri.getMidpoint(center);
      let valid = pointInPolygon(center, this.vertices);

      if (valid) {
        validTriangles.push(triangle);
      }
    });

    return validTriangles;
  }

  getDenaunayTriangles(points, reverseWinding) {
    let xFunc = point => {
      return point.x;
    };

    let yFunc = point => {
      return point.y;
    };

    let delaunay = Delaunator.from(points, xFunc, yFunc);

    let triangles = this.generateTriangleArray(delaunay.triangles, reverseWinding);
    triangles = this.removeInvalidTriangles(points, triangles);

    return triangles;
  }

  calculateVolume(updateValue) {
    this.calculatingVolume = true;
    this.measurement.controller.refreshMeasurementList();

    // Create a more dense version of our boundary polygon
    let denseBounds = getDensePolygon(this.vertices, this.spacing);

    // Query points for this bounding box
    let points = this.pointCloud.pointsInRadius(this.bounds.center,
      this.bounds.size / 2.0);

    // Quick filter by heights
    points = this.filterByHeight(points);

    // Get the points inside a polygon only
    points = this.pointCloud.pointsInPolygon(points, this.vertices);

    // Calculate DTM for points, in a 2d grid
    points = this.pointCloud.getSampledPoints(points, true, point => {
      let gridX = Math.floor(point.x / this.spacing);
      let gridY = Math.floor(point.y / this.spacing);
      let key = `${gridX}-${gridY}`;
      return key;
    });

    // Remove sparse outliers and continue
    this.removeOutliersAsync(points, points => {
      this.asyncFinished(points, denseBounds, updateValue);
    });
  }

  asyncFinished(points, denseBounds = [], updateValue = true) {
    this.destroyWorker();

    if (points.length !== 0) {
      this.webWorkerFinished(points, denseBounds, updateValue);
    }

    this.calculatingVolume = false;
    this.measurement.controller.refreshMeasurementList();
  }

  /** @private */
  webWorkerFinished(pointsArray, denseBounds, updateValue) {
    let points = [];
    pointsArray.forEach(point => {
      points.push(new Vector3().copy(point));
    });

    // Combine highest points + dense boundary
    points = denseBounds.concat(points);

    // Calculate upper mesh with reverse winding order
    let trianglesUpper = this.getDenaunayTriangles(points, true);

    // Calculate lower mesh
    let trianglesLower = this.getDenaunayTriangles(denseBounds, false);

    // Combine upper and lower triangles
    let trianglesCombined = [];
    trianglesCombined = trianglesCombined.concat(trianglesLower);
    trianglesCombined = trianglesCombined.concat(trianglesUpper);

    // this.drawDebugPoints(points);
    this.drawMesh(points, trianglesUpper);

    this.calculated = true;

    if (updateValue) {
      let volume = this.getVolume(points, trianglesCombined);
      this.value = volume;
    }

    // Update shaders (hide area highlight)
    this.measurement.controller.updateAreaShaders();
  }

  /** @private */
  stopCalculation() {
    if (this.calculatingVolume) {
      this.asyncFinished([]);
    }
  }

  clear() {
    this.scene.remove(this.pointswireframe);
    this.scene.remove(this.pointcloudmesh);
    this.pointswireframe = null;
    this.pointcloudmesh = null;
    this.value = null;
    this.calculatingVolume = false;
    this.calculated = false;
  }
}

class SurfaceGenerator {
  private measurement: MeasurementArea;
  private changeRequired = 0.01;
  private renderInScene = false;
  private indices = [];
  private meshes = [];

  constructor(measurement) {
    this.measurement = measurement;
    this.clear();
  }

  get scene() {
    let measurement = this.measurement;
    return measurement.controller.scene;
  }

  get area() {
    let area = 0;
    let vertices = this.measurement.localVertices3D;

    this.indices.forEach(indices => {
      let triangle = new Triangle(
        vertices[indices[0]],
        vertices[indices[1]],
        vertices[indices[2]]
      );
      area += triangle.getArea();
    });

    return area;
  }

  get valid() {
    return this.indices.length > 0;
  }

  clear() {
    this.removeMeshesFromScene();
    this.indices = [];
    this.meshes = [];
  }

  addMeshesToScene() {
    if (!this.meshes || !this.renderInScene) {
      return;
    }

    this.meshes.forEach(mesh => {
      this.scene.add(mesh);
    });
  }

  removeMeshesFromScene() {
    if (!this.meshes || !this.renderInScene) {
      return;
    }

    this.meshes.forEach(mesh => {
      this.scene.remove(mesh);
    });
  }

  generateMesh(triangle) {
    let hexColor = randomHexColor(); // TODO: remove random coloring later

    var geom = new Geometry();
    var normal = new Vector3();
    triangle.getNormal(normal);
    geom.vertices.push(triangle.a);
    geom.vertices.push(triangle.b);
    geom.vertices.push(triangle.c);
    geom.faces.push(new Face3(0, 1, 2, normal));
    var mesh = new Mesh(
      geom, new MeshBasicMaterial({
        color: hexColor,
        side: DoubleSide
      })
    );

    return mesh;
  }

  optimize(vertices, triangleIndices) {
    let verticesModified = false;
    let numIndices = triangleIndices.length;

    for (var triIndexA = 0; triIndexA < numIndices; triIndexA++) {
      for (var triIndexB = 0; triIndexB < numIndices; triIndexB++) {
        if (verticesModified) {
          continue;
        }

        let triA = triangleIndices[triIndexA];
        let triB = triangleIndices[triIndexB];

        let vertSet = new Set([...triA, ...triB]);
        let allVerts = Array.from(vertSet);
        if (allVerts.length !== 4) {
          continue;
        }

        let sharedVerts = triA.filter(x => triB.includes(x));
        let uniqueVerts = allVerts.filter(x => !sharedVerts.includes(x));

        let modTriA = [...uniqueVerts, sharedVerts[0]];
        let modTriB = [...uniqueVerts, sharedVerts[1]];

        let t1 = new Triangle(vertices[triA[0]],
          vertices[triA[1]], vertices[triA[2]]);
        let t2 = new Triangle(vertices[triB[0]],
          vertices[triB[1]], vertices[triB[2]]);
        let oldArea = t1.getArea() + t2.getArea();

        let t3 = new Triangle(vertices[modTriA[0]],
          vertices[modTriA[1]], vertices[modTriA[2]]);
        let t4 = new Triangle(vertices[modTriB[0]],
          vertices[modTriB[1]], vertices[modTriB[2]]);
        let newArea = t3.getArea() + t4.getArea();

        // New area must be smaller
        if (newArea > oldArea) {
          continue;
        }

        // Difference between areas must be non-trivial
        let diff = (oldArea - newArea) / oldArea;
        if (diff < this.changeRequired) {
          continue;
        }

        triangleIndices[triIndexA] = modTriA;
        triangleIndices[triIndexB] = modTriB;
        verticesModified = true;
      }
    }

    if (verticesModified) {
      return this.optimize(vertices, triangleIndices);
    }

    return triangleIndices;
  }

  generate() {
    let vertices = this.measurement.localVertices3D;
    let numVertices = vertices.length;
    let triangles = [];

    if (numVertices === 3) {
      triangles.push([0, 1, 2]);
    } else if (numVertices > 3) {
      let points = calculatePointsPCA(vertices, true);
      let indices = earcut(points, null, 3);
      let deviation = earcut.deviation(points, null, 3, indices);

      // Deviation must be nearly zero to be valid
      if (deviation > 1E-6) {
        return;
      }

      let triangleIndices = [];
      let numTriangles = indices.length / 3;
      for (var i = 0; i < numTriangles; i++) {
        triangleIndices.push([
          indices[3 * i],
          indices[3 * i + 1],
          indices[3 * i + 2]
        ]);
      }

      // Optimized surface meshes
      triangleIndices = this.optimize(vertices, triangleIndices);

      triangleIndices.forEach(triVertices => {
        triangles.push(triVertices);
      });
    }

    let meshes = [];
    triangles.forEach(indices => {
      let triangle = new Triangle(
        vertices[indices[0]],
        vertices[indices[1]],
        vertices[indices[2]]
      );
      let mesh = this.generateMesh(triangle);
      meshes.push(mesh);
    });

    this.indices = triangles;
    this.meshes = meshes;

    this.addMeshesToScene();
  }
}

const getDensePolygon = (vertices, spacing) => {
  let densePolygonTemp = [];

  vertices.forEach((start, index) => {
    let nextIndex = (index + 1) % vertices.length;

    let end = vertices[nextIndex];
    let direction = new Vector3().add(end).sub(start);
    let vectorLength = direction.length();
    let steps = Math.floor(vectorLength / spacing);
    let increment = vectorLength / steps;
    direction.setLength(1.0);

    if (steps === 0) {
      densePolygonTemp.push(start);
      return;
    }

    for (var i = 0; i <= steps; i++) {
      let directionIncrement = new Vector3().add(direction)
        .multiplyScalar(increment * i);
      let final = new Vector3().add(start).add(directionIncrement);
      densePolygonTemp.push(final);
    }
  });

  let densePolygon = [];
  densePolygonTemp.forEach((point, index) => {
    if (index === 0) {
      densePolygon.push(point);
    } else {
      let old = densePolygonTemp[index - 1];
      let distance = point.distanceTo(old);
      if (distance > 0) {
        densePolygon.push(point);
      }
    }
  });

  return densePolygonTemp;
};
